import { useEffect, useMemo, useState } from "react";
import { useI18n } from "../i18n/LanguageContext";
import { Footer } from "./parts/Footer";
import { LangSegment } from "./parts/LangSegment";
import { labelFor } from "./parts/labels";
import { rootCssVars, themeColors } from "./parts/themeTokens";
import { CardsView } from "./views/CardsView";
import { HeroView } from "./views/HeroView";
import { InlineView } from "./views/InlineView";
import { StackedView } from "./views/StackedView";
import type { StatItemData } from "./views/types";
import { useNetworkStats } from "./hooks/useNetworkStats";
import { useAutosize } from "./hooks/useAutosize";
import type { StatsConfig, Theme } from "./types";

export function StatsWidget({ cfg }: { cfg: StatsConfig }) {
  const { t, lang, setLang } = useI18n();
  const { stats, isLoading, isFetching, error, updatedAt, refetch } = useNetworkStats(
    cfg.refreshSec
  );
  useAutosize(cfg.autosize);

  // Apply explicit lang from config when not "auto"
  useEffect(() => {
    if (cfg.lang !== "auto" && cfg.lang !== lang) setLang(cfg.lang);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg.lang]);

  // Resolve theme + dark mode (auto follows prefers-color-scheme)
  const dark = useDarkMode(cfg.theme === "auto");
  const resolvedTheme: Theme = cfg.theme === "auto" ? (dark ? "dark" : "light") : cfg.theme;

  const items: StatItemData[] = useMemo(() => {
    if (!stats) return [];
    const map = {
      cities: stats.cities,
      locations: stats.locations,
      ac: stats.acSockets,
      dc: stats.dcSockets,
    } as const;
    return cfg.show.map((k) => ({
      k,
      value: map[k],
      label: labelFor(k, cfg, t, lang),
    }));
  }, [stats, cfg, t, lang]);

  const View =
    cfg.mode === "inline"
      ? InlineView
      : cfg.mode === "hero"
        ? HeroView
        : cfg.mode === "stacked"
          ? StackedView
          : CardsView;

  return (
    <div
      className="lm-stats relative h-screen w-screen overflow-hidden"
      style={rootCssVars(cfg, dark)}
    >
      {!cfg.noLangToggle && (
        <div className="absolute right-2 top-2 z-10">
          <LangSegment theme={resolvedTheme} lang={lang} setLang={setLang} />
        </div>
      )}
      <div className="flex h-full w-full flex-col">
        <div className="flex-1 min-h-0">
          {isLoading || items.length === 0 ? (
            <SkeletonStrip cfg={cfg} />
          ) : (
            <View cfg={cfg} items={items} theme={resolvedTheme} />
          )}
        </div>
        {cfg.brand !== "none" && (
          <Footer cfg={cfg} theme={resolvedTheme} updatedAt={updatedAt} isFetching={isFetching} t={t} />
        )}
      </div>
      {error && (
        <div className="absolute inset-x-0 bottom-0 mx-auto max-w-md p-3">
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-700">
            {error.message}
            <button onClick={refetch} className="ml-2 font-medium underline underline-offset-2">
              {t("errorRetry")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function SkeletonStrip({ cfg }: { cfg: StatsConfig }) {
  const c = themeColors(cfg.theme === "auto" ? "light" : cfg.theme, false);
  const tone = cfg.theme === "dark" ? "bg-white/10" : "bg-ink-100";
  const cols = cfg.show.length;
  const colsCls = cols === 1 ? "grid-cols-1" : cols === 2 ? "grid-cols-2" : cols === 3 ? "grid-cols-3" : "grid-cols-2 sm:grid-cols-4";
  return (
    <div className={"grid h-full gap-3 p-4 sm:p-5 " + colsCls}>
      {Array.from({ length: cols || 4 }).map((_, i) => (
        <div
          key={i}
          className="rounded-2xl border p-4 sm:p-5"
          style={{ background: c.cardBg, borderColor: c.cardBorder }}
        >
          <div className={"h-9 w-20 rounded-md " + tone + " animate-pulse"} />
          <div className={"mt-3 h-3 w-16 rounded-md " + tone + " animate-pulse"} />
        </div>
      ))}
    </div>
  );
}

function useDarkMode(active: boolean): boolean {
  const [dark, setDark] = useState<boolean>(() => {
    if (typeof window === "undefined" || !active) return false;
    return !!window.matchMedia?.("(prefers-color-scheme: dark)").matches;
  });
  useEffect(() => {
    if (!active || typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => setDark(mq.matches);
    mq.addEventListener?.("change", onChange);
    setDark(mq.matches);
    return () => mq.removeEventListener?.("change", onChange);
  }, [active]);
  return dark;
}
